import React, { useState, useEffect, useCallback } from 'react';
import * as I from 'lucide-react';
import { useAuth } from '../context/AuthContext';
// Corrected import path assuming UI components are in src/components/UI
import { PageHeader, StatsCard, Card, Button, LoadingSpinner, EmptyState, Badge } from '../components/UI';
import S3Uploader from '../components/S3Uploader';
import api from '../api';

const DashboardPage = ({ navigate }) => {
    const { user, isAuditor } = useAuth();
    const [stats, setStats] = useState([]);
    const [recentActivity, setRecentActivity] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showManualUpload, setShowManualUpload] = useState(false);

    // Fetch Dashboard Data Function
    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        setError('');
        setStats([]);
        setRecentActivity([]);

        console.log('=== Starting Dashboard Fetch ===');

        try {
            // Check authentication first
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Not authenticated. Please log in again.');
            }

            console.log('Authentication check - Token exists:', !!token);

            // Fetch basic data with individual error handling
            let recentInvoices = [];
            let totalInvoicesCount = 0;
            let totalVendorsCount = 0;
            let pendingApprovalsCount = 0;
            let readyForPaymentCount = 0;

            try {
                // Fetch recent invoices with better error handling
                console.log('Fetching recent invoices...');
                const recentRes = await api.get('/invoices?page=0&size=3&sort=dateReceived,desc');

                // Handle different response formats
                let invoicesData;
                if (Array.isArray(recentRes.data)) {
                    invoicesData = recentRes.data;
                    console.log('Response format: Array');
                } else if (recentRes.data?.content) {
                    invoicesData = recentRes.data.content;
                    console.log('Response format: Paginated');
                } else {
                    invoicesData = recentRes.data ? [recentRes.data] : [];
                    console.log('Response format: Single object or empty');
                }

                recentInvoices = invoicesData || [];
                console.log(`SUCCESS: Fetched ${recentInvoices.length} recent invoices from database`);

                if (recentInvoices.length > 0) {
                    console.log('First invoice sample:', recentInvoices[0]);
                }
            } catch (err) {
                console.warn('Could not fetch recent invoices:', err.message);
                // Continue without recent invoices data
            }

            try {
                // Fetch total counts from paginated endpoints
                const [invoicesRes, vendorsRes] = await Promise.all([
                    api.get('/invoices?page=0&size=1').catch(() => ({ data: { totalElements: 0 } })),
                    api.get('/vendors?page=0&size=1').catch(() => ({ data: { totalElements: 0 } }))
                ]);

                totalInvoicesCount = invoicesRes.data?.totalElements || 0;
                totalVendorsCount = vendorsRes.data?.totalElements || 0;
                console.log(`Fetched counts - Invoices: ${totalInvoicesCount}, Vendors: ${totalVendorsCount}`);
            } catch (err) {
                console.warn('Could not fetch counts:', err.message);
            }

            try {
                // Fetch pending approval and ready for payment counts from all invoices
                const allInvoicesRes = await api.get('/invoices?page=0&size=1000').catch(() => ({ data: { content: [] } }));

                let allInvoices = [];
                if (Array.isArray(allInvoicesRes.data)) {
                    allInvoices = allInvoicesRes.data;
                } else if (allInvoicesRes.data?.content) {
                    allInvoices = allInvoicesRes.data.content;
                } else {
                    allInvoices = allInvoicesRes.data ? [allInvoicesRes.data] : [];
                }

                // Count invoices by status
                pendingApprovalsCount = allInvoices.filter(inv => {
                    const status = inv.status?.toLowerCase();
                    return status === 'pending_approval' || status === 'pending';
                }).length;

                const approvedCount = allInvoices.filter(inv => {
                    const status = inv.status?.toLowerCase();
                    return status === 'approved';
                }).length;

                const rejectedCount = allInvoices.filter(inv => {
                    const status = inv.status?.toLowerCase();
                    return status === 'rejected';
                }).length;

                console.log(`Fetched invoice counts - Total: ${allInvoices.length}, Pending: ${pendingApprovalsCount}, Approved: ${approvedCount}, Rejected: ${rejectedCount}`);
                console.log('Invoice status distribution:', allInvoices.reduce((acc, inv) => {
                    const status = inv.status?.toLowerCase() || 'unknown';
                    acc[status] = (acc[status] || 0) + 1;
                    return acc;
                }, {}));

                readyForPaymentCount = approvedCount;
                // pendingApprovalsCount is already set from the filter above

            } catch (err) {
                console.warn('Could not fetch invoice status counts:', err.message);
                
                // CORRECTED LOGIC:
                // Don't estimate with percentages. If this fetch fails, set counts to 0.
                pendingApprovalsCount = 0;
                readyForPaymentCount = 0;
            }

            // Check if we got any real data from the backend
            const hasRealData = recentInvoices.length > 0 || totalInvoicesCount > 0 || totalVendorsCount > 0 || pendingApprovalsCount > 0 || readyForPaymentCount > 0;

            // If no backend connection at all, use fallback
            if (!hasRealData) {
                console.log('No backend data available, using fallback for stats only');
                setFallbackData();
                return;
            }

            // Prepare stats based on user role - USE REAL DATA
            const allStats = [
                {
                    title: 'Pending Approvals',
                    value: pendingApprovalsCount.toString(),
                    icon: I.Clock3,
                    color: 'warning',
                    action: () => navigate('approvals'),
                    roles: ['ADMIN']
                },
                {
                    title: 'Ready for Payment',
                    value: readyForPaymentCount.toString(),
                    icon: I.Banknote,
                    color: 'success',
                    action: () => navigate('payments'),
                    roles: ['ADMIN', 'PAYABLES_EXECUTIVE']
                },
                {
                    title: 'Total Vendors',
                    value: totalVendorsCount.toString(),
                    icon: I.Building2,
                    color: 'primary',
                    action: () => navigate('vendors'),
                    roles: ['ADMIN', 'PAYABLES_EXECUTIVE', 'AUDITOR']
                },
                {
                    title: 'Total Invoices',
                    value: totalInvoicesCount.toString(),
                    icon: I.FileText,
                    color: 'info',
                    action: () => navigate('invoices'),
                    roles: ['ADMIN', 'PAYABLES_EXECUTIVE', 'AUDITOR']
                }
            ];

            // Filter stats based on user roles
            const visibleStats = allStats.filter(stat =>
                !stat.roles || (user?.roles && stat.roles.some(role => user.roles.includes(role)))
            );

            setStats(visibleStats);

            // Prepare recent activity from REAL invoice data
            if (recentInvoices.length > 0) {
                const activity = recentInvoices.slice(0, 3).map((inv, index) => {
                    // Helper function to get invoice number from various possible field names
                    const getInvoiceNumber = (invoice) => {
                        const possibleFields = ['invoiceNumber', 'number', 'invoiceNo', 'invNumber', 'id'];
                        for (const field of possibleFields) {
                            if (invoice[field] && String(invoice[field]).trim()) {
                                return String(invoice[field]);
                            }
                        }
                        return `Invoice-${invoice.id || index + 1}`;
                    };

                    // Helper function to get vendor info
                    const getVendorInfo = (invoice) => {
                        // Use vendorName if it exists
                        if (invoice.vendorName) {
                            return invoice.vendorName;
                        }
                        const possibleFields = ['vendorId', 'vendor', 'supplierId', 'supplier'];
                        for (const field of possibleFields) {
                            if (invoice[field] !== undefined && invoice[field] !== null) {
                                return `Vendor ID: ${invoice[field]}`;
                            }
                        }
                        return 'Unknown Vendor';
                    };

                    // Helper function to get status
                    const getStatus = (invoice) => {
                        const possibleFields = ['status', 'state', 'invoiceStatus'];
                        for (const field of possibleFields) {
                            if (invoice[field]) {
                                return invoice[field];
                            }
                        }
                        return 'Unknown';
                    };

                    // Helper function to get date
                    const getDate = (invoice) => {
                        const possibleFields = ['invoiceDate', 'date', 'createdDate', 'issueDate', 'dateReceived'];
                        for (const field of possibleFields) {
                            if (invoice[field]) {
                                return invoice[field];
                            }
                        }
                        return new Date().toISOString().split('T')[0];
                    };

                    // Helper function to get amount
                    const getAmount = (invoice) => {
                        const possibleFields = ['totalAmount', 'amount', 'total', 'invoiceAmount', 'grandTotal', 'netAmount'];
                        for (const field of possibleFields) {
                            if (invoice[field] !== undefined && invoice[field] !== null && invoice[field] !== 0) {
                                return invoice[field];
                            }
                        }
                        return 0;
                    };

                    return {
                        id: inv.id || index,
                        action: `Invoice ${getInvoiceNumber(inv)} (${getVendorInfo(inv)}) - ${getStatus(inv)}`,
                        time: formatRelativeTime(getDate(inv)),
                        type: getActivityType(getStatus(inv)),
                        amount: getAmount(inv),
                        vendorName: inv.vendorName || `Vendor ${inv.vendorId || 'Unknown'}`
                    };
                });

                console.log('Generated recent activity from real data:', activity);
                setRecentActivity(activity);
            } else {
                console.log('No recent invoices found');
                setRecentActivity([]);
            }

            console.log('Dashboard data loaded successfully with real data');

        } catch (err) {
            console.error('Dashboard fetch error:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to load dashboard data';

            if (err.response?.status === 401) {
                localStorage.removeItem('token');
                window.location.href = '/login';
                return;
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
            console.log('=== Dashboard Fetch Complete ===');
        }
    }, [navigate, user?.roles]);

    // Fallback data function when backend is not available
    const setFallbackData = useCallback(() => {
        console.log('Using fallback data for dashboard');
        const fallbackStats = [
            {
                title: 'Pending Approvals',
                value: '0',
                icon: I.Clock3,
                color: 'warning',
                action: () => navigate('approvals'),
                roles: ['ADMIN']
            },
            {
                title: 'Ready for Payment',
                value: '0',
                icon: I.Banknote,
                color: 'success',
                action: () => navigate('payment'),
                roles: ['ADMIN', 'PAYABLES_EXECUTIVE']
            },
            {
                title: 'Total Vendors',
                value: '0',
                icon: I.Building2,
                color: 'primary',
                action: () => navigate('vendors'),
                roles: ['ADMIN', 'PAYABLES_EXECUTIVE', 'AUDITOR']
            },
            {
                title: 'Total Invoices',
                value: '0',
                icon: I.FileText,
                color: 'info',
                action: () => navigate('invoices'),
                roles: ['ADMIN', 'PAYABLES_EXECUTIVE', 'AUDITOR']
            }
        ];

        // Filter stats based on user roles
        const visibleStats = fallbackStats.filter(stat =>
            !stat.roles || (user?.roles && stat.roles.some(role => user.roles.includes(role)))
        );

        setStats(visibleStats);

        // DON'T set dummy activity data - show empty state instead
        setRecentActivity([]);

        setLoading(false);
    }, [navigate, user?.roles]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]); // Fetch data on mount

    // Helper Functions
    const formatRelativeTime = (dateString) => {
        if (!dateString) return 'Recently';
        try {
            // Try to parse the date. Handle ISO strings and plain dates.
            let date;
            if (dateString.includes('T')) {
                date = new Date(dateString);
            } else {
                // Assume YYYY-MM-DD and parse as local time
                const parts = dateString.split('-');
                if (parts.length === 3) {
                    date = new Date(parts[0], parts[1] - 1, parts[2]);
                } else {
                    date = new Date(dateString); // Fallback
                }
            }
            
            if (isNaN(date)) { // Check for invalid date
                 return 'Invalid date';
            }

            const now = new Date();
            const diffMs = now - date;
            const diffSecs = Math.round(diffMs / 1000);
            const diffMins = Math.round(diffSecs / 60);
            const diffHours = Math.round(diffMins / 60);
            const diffDays = Math.round(diffHours / 24);

            if (diffSecs < 60) return 'Just now';
            if (diffMins < 60) return `${diffMins} min ago`;
            if (diffHours < 24) return `${diffHours} hr ago`;
            if (diffDays === 1) return 'Yesterday';
            if (diffDays < 7) return `${diffDays} days ago`;
            return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

        } catch (e) {
            console.warn("Error formatting relative time:", e);
            return 'Invalid date';
        }
    };

    const formatCurrency = (amount) => {
        if (!amount || amount === 0) return '';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const getActivityType = (status) => {
        switch (status?.toUpperCase()) {
            case 'APPROVED': return 'approval';
            case 'PAID': return 'payment';
            case 'REJECTED': return 'error';
            case 'PENDING_APPROVAL': return 'pending';
            case 'PENDING': return 'pending';
            default: return 'invoice';
        }
    };


    const handleManualUpload = () => {
        setShowManualUpload(true);
    };

    const quickActions = [
        {
            title: 'Manual Upload',
            description: 'Open manual invoice folder',
            icon: I.Upload,
            action: handleManualUpload,
            color: 'primary',
            mutating: true // hidden for read-only AUDITOR
        },
        {
             title: 'Add Vendor',
             description: 'Register new vendor',
             icon: I.UserPlus,
             mutating: true, // hidden for read-only AUDITOR
             action: () => {
                 // First update the page state
                 if (typeof navigate === 'function') {
                     navigate('vendors');
                     // Then open the add modal after a short delay
                     setTimeout(() => {
                         const event = new CustomEvent('openAddVendorModal');
                         window.dispatchEvent(event);
                     }, 100);
                 }
             },
             color: 'success' 
        },
        { 
            title: 'Search Invoices', 
            description: 'Find specific invoices', 
            icon: I.Search, 
            action: () => navigate('invoices'), 
            color: 'info' 
        },
    ];

    return (
        <div className="p-6 lg:p-8 space-y-8 bg-gray-50 min-h-screen"> {/* Added bg color */}
            <PageHeader
                title={`Welcome back, ${user?.username || 'User'}!`}
                subtitle="Here's a summary of your accounts payable activities"
            />

             {/* Loading State Overlay (Optional - instead of replacing content) */}
            {/* {loading && <div className="fixed inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50"><LoadingSpinner size="xl" /></div>} */}

             {/* Error State - Prominent Display */}
            {error && !loading && (
                <Card className="text-center py-12 bg-red-50 border border-red-200 shadow-md">
                    <div className="text-red-600 mb-4"> <I.AlertTriangle className="w-16 h-16 mx-auto mb-3 animate-pulse" /> </div>
                    <h3 className="text-xl font-semibold text-red-800 mb-2">AP Dashboard Connection Error</h3>
                    <p className="text-red-700 mb-6 max-w-md mx-auto">{error}</p>
                    <Button onClick={fetchDashboardData} variant="error" className="bg-red-600 hover:bg-red-700 text-white">
                         <I.RefreshCw className="w-4 h-4 mr-2" /> Try Again
                    </Button>
                </Card>
            )}

            {/* Content Area - Only render if no error and potentially show skeleton/partial load */}
            {!error && (
                <>
                    {/* Stats Grid - Show placeholders while loading */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {loading && stats.length === 0 ? (
                             // Skeleton Loader for Stats
                             Array.from({ length: 4 }).map((_, index) => (
                                <Card key={index} className="animate-pulse">
                                    <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
                                    <div className="h-12 bg-gray-300 rounded w-1/2"></div>
                                </Card>
                             ))
                        ) : (
                            // Actual Stats
                            stats.map(stat => (
                                 <div key={stat.title} onClick={stat.action} className="cursor-pointer transition transform hover:scale-105 duration-200 shadow-sm hover:shadow-lg">
                                    <StatsCard
                                        title={stat.title}
                                        value={stat.value}
                                        icon={stat.icon}
                                        color={stat.color}
                                    />
                                 </div>
                            ))
                        )}
                         {/* Placeholder if no stats visible and not loading */}
                         {!loading && stats.length === 0 && !error && (
                            <Card className="col-span-full text-center text-neutral-500 py-8">No dashboard stats available for your role.</Card>
                         )}
                    </div>

                    {/* Main Content Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Quick Actions */}
                        <div className="lg:col-span-2">
                             <Card className="shadow-sm"> {/* Quick Actions Card */}
                                 <h2 className="text-xl font-semibold text-gray-800 mb-1">Quick Actions</h2>
                                 <p className="text-sm text-gray-600 mb-6">Frequently used operations</p>
                                 <div className="grid grid-cols-1 sm:grid-cols-3 gap-4"> {/* Adjusted grid */}
                                      {quickActions.filter(action => !(isAuditor && action.mutating)).map((action, index) => (
                                         <div key={index} onClick={action.action} className="p-4 border border-gray-200 rounded-lg hover:shadow-lg hover:border-primary-300 transition cursor-pointer group flex flex-col items-start text-left bg-white">
                                             <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${ action.color === 'primary' ? 'bg-blue-100 text-blue-600' : action.color === 'success' ? 'bg-green-100 text-green-600' : action.color === 'info' ? 'bg-sky-100 text-sky-600' : 'bg-gray-100 text-gray-600' }`}>
                                                 <action.icon className="w-5 h-5" />
                                             </div>
                                             <h3 className="font-medium text-gray-900 group-hover:text-primary-600 transition-colors">{action.title}</h3>
                                             <p className="text-sm text-gray-600 mt-1 flex-grow">{action.description}</p>
                                         </div>
                                      ))}
                                 </div>
                             </Card>
                        </div>

                        {/* Recent Activity */}
                        <div>
                             <Card className="shadow-sm"> {/* Recent Activity Card */}
                                 <h2 className="text-xl font-semibold text-gray-800 mb-1">Recent Activity</h2>
                                 <p className="text-sm text-gray-600 mb-6">Latest system activities</p>
                                 {loading && recentActivity.length === 0 ? (
                                     // Skeleton loader for activity
                                     <div className="space-y-4 animate-pulse">
                                          {Array.from({length: 4}).map((_, i) => (
                                             <div key={i} className="flex items-start space-x-3">
                                                 <div className="w-2 h-2 rounded-full mt-1.5 bg-gray-300"></div>
                                                 <div className="flex-1">
                                                     <div className="h-4 bg-gray-300 rounded w-3/4 mb-1"></div>
                                                     <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                                 </div>
                                             </div>
                                          ))}
                                     </div>
                                 ) : !loading && recentActivity.length > 0 ? (
                                      <div className="space-y-4">
                                          {recentActivity.map((activity) => (
                                             <div key={activity.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                                                 {/* Status Indicator */}
                                                 <div className={`mt-1.5 flex-shrink-0 w-2.5 h-2.5 rounded-full ${
                                                     activity.type === 'approval' ? 'bg-green-500' :
                                                     activity.type === 'payment' ? 'bg-blue-500' :
                                                     activity.type === 'error' ? 'bg-red-500' :
                                                     activity.type === 'pending' ? 'bg-yellow-500' :
                                                     'bg-gray-400'
                                                 }`} title={activity.type}></div>
                                                 {/* Text Content */}
                                                 <div className="flex-1 min-w-0">
                                                     <p className="text-sm text-gray-800 font-medium" title={activity.action}>
                                                         {activity.action}
                                                     </p>
                                                     {activity.amount > 0 && (
                                                         <p className="text-xs text-gray-600 mt-1">
                                                             Amount: {formatCurrency(activity.amount)}
                                                         </p>
                                                     )}
                                                     <p className="text-xs text-gray-500">{activity.time}</p>
                                                 </div>
                                             </div>
                                          ))}
                                          {/* View All Button */}
                                          <div className="mt-6 pt-4 border-t border-gray-200">
                                              <Button variant="secondary" className="w-full text-sm" onClick={() => navigate('activity')}>
                                                  View All Activity
                                              </Button>
                                          </div>
                                      </div>
                                 ) : (
                                      <EmptyState icon={I.ListChecks} title="No Recent Activity" description="System activities will appear here." small />
                                 )}
                             </Card>
                        </div>
                    </div>

                    {/* System Status (Optional - Only show if relevant) */}
                    <Card className="shadow-sm">
                         <div className="flex items-center justify-between">
                             <div>
                                 <h3 className="text-lg font-semibold text-gray-800">System Status</h3>
                                 <p className="text-sm text-gray-600 mt-1">All monitored systems operational</p>
                             </div>
                             <div className="flex items-center space-x-2">
                                 <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                                 <span className="text-sm font-medium text-green-700">Online</span>
                             </div>
                         </div>
                    </Card>
                </>
            )}
            {showManualUpload && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <h2 className="text-lg font-semibold text-gray-800">Manual Invoice Uploads</h2>
                            <button
                                type="button"
                                onClick={() => setShowManualUpload(false)}
                                className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                                aria-label="Close"
                            >
                                <I.X className="w-5 h-5" />
                            </button>
                        </div>
                        <S3Uploader 
                            onClose={() => setShowManualUpload(false)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default DashboardPage;
